import React, { useMemo, useState } from "react";
import {
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
} from "recharts";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import {
  People,
  Assignment,
  CheckCircle,
  Business,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import MetricCard from "./MetricCard";
import ChartCard from "./ChartCard";

const chartColors = {
  accepted: "#50C878",
  partial: "#f6b580", // using the new partial color
  rejected: "#F44336",
  pending: "#2196F3",
  errored: "#9C27B0",
  active: "#50C878",
  notActive: "#F44336",
  primary: "#3F51B5",
  secondary: "#607D8B",
  cancelled: "#ff7fff",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-100 shadow-xl rounded-lg min-w-[120px]">
        <p className="text-[10px] font-bold text-gray-400 mb-1 uppercase border-b pb-1">
          {label}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 mt-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.payload?.color || entry.color }}
            />
            <div className="flex gap-1 text-[11px] font-semibold text-gray-700">
              <span>{entry.name}:</span>
              <span className="font-bold">{entry.value}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const preAuthBreakdownStatuses = [
  { key: "approved", label: "Approved", color: "#3FAE61" },
  { key: "pending", label: "Pending", color: "#F59E0B" },
  { key: "rejected", label: "Rejected", color: "#EF2B2D" },
  { key: "cancelled", label: "Cancelled", color: "#737A84" },
  { key: "partial", label: "Partial", color: "#F6B580" },
  { key: "errored", label: "Errored", color: "#9C27B0" },
];

const formatPreAuthType = (type = "") =>
  type
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatRate = (value) => `${Number(value || 0).toFixed(1)}%`;

const PreAuthBreakdownChart = ({ rows = [], totalPreAuth, t }) => {
  const sortedRows = [...rows].sort((a, b) => (b.total || 0) - (a.total || 0));

  if (!sortedRows.length) {
    return (
      <Box className="flex h-[250px] items-center justify-center text-sm font-semibold text-gray-400">
        {t("No preauth breakdown found")}
      </Box>
    );
  }

  const chartData = sortedRows.map((row) => ({
    ...row,
    name: t(formatPreAuthType(row.type || "unknown")),
  }));

  return (
    <Box className="w-full overflow-x-auto">
      <Box className="min-w-[760px]">
        <Box className="mb-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {preAuthBreakdownStatuses.map((status) => (
            <Box key={status.key} className="flex items-center gap-2">
              <Box className="h-3 w-3 rounded-sm" sx={{ backgroundColor: status.color }} />
              <Typography className="!text-[12px] !font-semibold !text-slate-600">
                {t(status.label)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box className="h-[360px]">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 28, right: 10, left: 0, bottom: 18 }}>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 700, fill: "#475569" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
              <Tooltip content={<CustomTooltip />} />
              {preAuthBreakdownStatuses.map((status, index) => (
                <Bar
                  key={status.key}
                  dataKey={status.key}
                  stackId="preauth"
                  fill={status.color}
                  name={t(status.label)}
                  barSize={80}
                  radius={index === preAuthBreakdownStatuses.length - 1 ? [3, 3, 0, 0] : 0}
                >
                  <LabelList
                    dataKey={status.key}
                    content={({ x, y, width, height, value, index: rowIndex }) => {
                      const rowTotal = chartData[rowIndex]?.total || 0;
                      const percentage = rowTotal ? (value / rowTotal) * 100 : 0;
                      if (!value || height < 28 || width < 28) return null;

                      return (
                        <text
                          x={x + width / 2}
                          y={y + height / 2}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#fff"
                          fontSize={10}
                          fontWeight={700}
                        >
                          <tspan x={x + width / 2} dy="-1">
                            {value.toLocaleString()}
                          </tspan>
                          <tspan x={x + width / 2} dy="12">
                            ({percentage.toFixed(1)}%)
                          </tspan>
                        </text>
                      );
                    }}
                  />
                  {index === preAuthBreakdownStatuses.length - 1 && (
                    <LabelList dataKey="total" position="top" fill="#1E293B" fontSize={14} fontWeight={700} />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

const PreAuthDistribution = ({ rows = [], totalPreAuth, t }) => {
  const distributionData = preAuthBreakdownStatuses.map((status) => ({
    name: t(status.label),
    value: rows.reduce((sum, row) => sum + (row[status.key] || 0), 0),
    color: status.color,
  }));
  const visibleData = distributionData.filter((item) => item.value > 0);

  return (
    <Box className="h-full rounded-lg border border-slate-200 bg-white p-3">
      <Typography className="!mb-2 !text-[14px] !font-bold !text-slate-700">
        {t("Preauth Distribution")}
      </Typography>
      <Box>
        <Box className="relative mx-auto h-[215px] w-[215px]">
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={visibleData}
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={98}
                dataKey="value"
                stroke="none"
              >
                {visibleData.map((entry, index) => (
                  <Cell key={`preauth-distribution-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <Box className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <Typography className="!text-[20px] !font-bold !text-slate-900">
              {(totalPreAuth || 0).toLocaleString()}
            </Typography>
            <Typography className="!text-[10px] !font-semibold !text-slate-500">
              {t("Total Pre-Auth")}
            </Typography>
          </Box>
        </Box>
        <Box className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-3 border-t border-slate-100 pt-3">
          {distributionData.map((item) => {
            const percentage = totalPreAuth ? (item.value / totalPreAuth) * 100 : 0;
            return (
              <Box key={item.name} className="flex min-w-[82px] items-start gap-2">
                <Box className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" sx={{ backgroundColor: item.color }} />
                <Box className="min-w-0">
                  <Typography className="truncate !text-[11px] !font-bold !text-slate-700">
                    {item.name}
                  </Typography>
                  <Typography className="!text-[11px] !font-semibold !text-slate-500">
                    {item.value.toLocaleString()} ({formatRate(percentage)})
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const EligibilityPayerBreakdown = ({
  rows = [],
  totalVerification,
  activeVerification,
  notActiveVerification,
  activeRate,
  t,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [rankMode, setRankMode] = useState("top");
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => (b.total || 0) - (a.total || 0)),
    [rows],
  );
  const totalPages = Math.max(Math.ceil(sortedRows.length / pageSize), 1);
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleRows = sortedRows.slice(startIndex, startIndex + pageSize);
  const topRows = useMemo(
    () =>
      [...rows]
        .sort((a, b) => (b.active_rate || 0) - (a.active_rate || 0))
        .slice(0, 3),
    [rows],
  );
  const bottomRows = useMemo(
    () =>
      [...rows]
        .sort((a, b) => (a.active_rate || 0) - (b.active_rate || 0))
        .slice(0, 3),
    [rows],
  );
  const rankRows = rankMode === "top" ? topRows : bottomRows;
  const distributionData = [
    {
      name: t("Active (Eligible)"),
      value: activeVerification || 0,
      color: chartColors.accepted,
    },
    {
      name: t("Not Active (Not Eligible)"),
      value: notActiveVerification || 0,
      color: chartColors.rejected,
    },
  ];

  const renderRankRows = (rankRows) =>
    rankRows.map((row, index) => (
      <Box
        key={`${row.payer_id || row.payer_name || index}-${index}`}
        className="grid grid-cols-[22px_1fr_110px_52px] items-center gap-2"
      >
        <Typography className="!text-[12px] !font-semibold !text-slate-500">
          {index + 1}
        </Typography>
        <Typography className="truncate !text-[12px] !font-semibold !text-slate-700">
          {row.payer_name || t("Unknown")}
        </Typography>
        <Box className="h-2 overflow-hidden rounded-full bg-slate-100">
          <Box
            className="h-full rounded-full bg-emerald-500"
            sx={{ width: `${Math.min(row.active_rate || 0, 100)}%` }}
          />
        </Box>
        <Typography className="!text-right !text-[12px] !font-bold !text-slate-700">
          {formatRate(row.active_rate)}
        </Typography>
      </Box>
    ));

  if (!sortedRows.length) {
    return (
      <Box className="flex h-[250px] items-center justify-center text-sm font-semibold text-gray-400">
        {t("No verification payer breakdown found")}
      </Box>
    );
  }

  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12} lg={8}>
        <Box className="w-full overflow-x-auto">
          <Box className="min-w-[820px]">
            <Box className="mb-3 grid grid-cols-[280px_1fr] items-center gap-4 px-1">
              <Typography className="!text-[12px] !font-bold !text-slate-600">
                {t("Insurance Company")}
              </Typography>
              <Box className="grid grid-cols-2">
                <Typography className="!text-[12px] !font-bold !text-slate-600">
                  {t("Active Verifications")}
                </Typography>
                <Typography className="!text-right !text-[12px] !font-bold !text-slate-600">
                  {t("Not Active Verifications")}
                </Typography>
              </Box>
              {/* <Typography className="!text-right !text-[12px] !font-bold !text-slate-600">
                {t("Total")}
              </Typography>
              <Typography className="!text-right !text-[12px] !font-bold !text-slate-600">
                {t("Eligibility %")}
              </Typography> */}
            </Box>

            <Box className="space-y-4">
              {visibleRows.map((row, index) => {
                const rowTotal = row.total || 0;
                const active = row.active || 0;
                const notActive = row.not_active || 0;
                const activePercent = rowTotal ? (active / rowTotal) * 100 : 0;
                const notActivePercent = rowTotal
                  ? (notActive / rowTotal) * 100
                  : 0;

                return (
                  <Box
                    key={`${row.payer_id || row.payer_name || index}`}
                    className="grid grid-cols-[280px_1fr] items-center gap-4"
                  >
                    <Box className="flex items-center gap-2">
                      <Box className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-primary">
                        <Business fontSize="small" />
                      </Box>
                      <Typography className="truncate !text-[13px] !font-bold !text-slate-700">
                        {row.payer_name || t("Unknown")}
                      </Typography>
                    </Box>

                    <Box className="flex h-9 overflow-hidden rounded bg-slate-100">
                      <Box
                        className="flex min-w-[3px] items-center justify-center bg-emerald-500 px-1 text-white"
                        sx={{ width: `${activePercent}%` }}
                        title={`${t("Active")}: ${active} (${formatRate(
                          activePercent,
                        )})`}
                      >
                        {activePercent >= 14 && (
                          <Typography className="!text-[11px] !font-bold !text-white">
                            {active.toLocaleString()} ({formatRate(activePercent)})
                          </Typography>
                        )}
                      </Box>
                      <Box
                        className="flex min-w-[3px] items-center justify-center bg-red-500 px-1 text-white"
                        sx={{ width: `${notActivePercent}%` }}
                        title={`${t("Not Active")}: ${notActive} (${formatRate(
                          notActivePercent,
                        )})`}
                      >
                        {notActivePercent >= 14 && (
                          <Typography className="!text-[11px] !font-bold !text-white">
                            {notActive.toLocaleString()} (
                            {formatRate(notActivePercent)})
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* <Typography className="!text-right !text-[13px] !font-bold !text-slate-700">
                      {rowTotal.toLocaleString()}
                    </Typography>
                    <Box className="flex items-center justify-end gap-2">
                      <Box
                        className="flex h-8 w-8 items-center justify-center rounded-full border-4"
                        sx={{
                          borderColor:
                            (row.active_rate || 0) >= activeRate
                              ? chartColors.accepted
                              : "#F59E0B",
                        }}
                      >
                        <Typography className="!text-[10px] !font-bold !text-slate-700">
                          {Math.round(row.active_rate || 0)}
                        </Typography>
                      </Box>
                      <Typography className="!min-w-[44px] !text-right !text-[13px] !font-bold !text-slate-700">
                        {formatRate(row.active_rate)}
                      </Typography>
                    </Box> */}
                  </Box>
                );
              })}
            </Box>

            <Box className="mt-5 flex items-center justify-center gap-6">
              <Box className="flex items-center gap-2">
                <Box className="h-3 w-3 rounded-sm bg-emerald-500" />
                <Typography className="!text-[12px] !font-semibold !text-slate-600">
                  {t("Active (Eligible)")}
                </Typography>
              </Box>
              <Box className="flex items-center gap-2">
                <Box className="h-3 w-3 rounded-sm bg-red-500" />
                <Typography className="!text-[12px] !font-semibold !text-slate-600">
                  {t("Not Active (Not Eligible)")}
                </Typography>
              </Box>
            </Box>

            <Box className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
              <Typography className="!text-[12px] !font-semibold !text-slate-500">
                {t("Showing")} {sortedRows.length ? startIndex + 1 : 0}{" "}
                {t("to")}{" "}
                {Math.min(startIndex + pageSize, sortedRows.length)} {t("of")}{" "}
                {sortedRows.length} {t("companies")}
              </Typography>

              <Box className="flex items-center gap-2">
                <IconButton
                  size="small"
                  disabled={currentPage === 1}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  className="!h-8 !w-8 !rounded-md !border !border-slate-200"
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>

                {Array.from({ length: totalPages }, (_, index) => index + 1)
                  .slice(0, 5)
                  .map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      onClick={() => setPage(pageNumber)}
                      className={`h-8 w-8 rounded-md border text-[12px] font-bold ${
                        currentPage === pageNumber
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-slate-200 bg-white text-slate-600"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  ))}

                <IconButton
                  size="small"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  className="!h-8 !w-8 !rounded-md !border !border-slate-200"
                >
                  <ChevronRight fontSize="small" />
                </IconButton>

                <Select
                  size="small"
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(1);
                  }}
                  sx={{
                    height: 32,
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#475569",
                    ".MuiSelect-select": { py: 0.5 },
                  }}
                >
                  {[5, 10, 15, 20].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size} / {t("page")}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Box className="grid h-full gap-2">
          <Box className="rounded-lg border border-slate-200 p-3">
            <Typography className="!mb-2 !text-[14px] !font-bold !text-slate-700">
              {t("Eligibility Distribution")}
            </Typography>
            <Box className="grid grid-cols-[175px_1fr] items-center gap-3">
              <Box className="relative h-[175px]">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={distributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={56}
                      outerRadius={80}
                      dataKey="value"
                      stroke="none"
                    >
                      {distributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <Box className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <Typography className="!text-[20px] !font-bold !text-slate-900">
                    {(totalVerification || 0).toLocaleString()}
                  </Typography>
                  <Typography className="!text-[10px] !font-semibold !text-slate-500">
                    {t("Total Verifications")}
                  </Typography>
                </Box>
              </Box>
              <Box className="space-y-3">
                {distributionData.map((item) => {
                  const percent = totalVerification
                    ? (item.value / totalVerification) * 100
                    : 0;
                  return (
                    <Box key={item.name} className="flex items-start gap-2">
                      <Box
                        className="mt-1 h-2.5 w-2.5 rounded-full"
                        sx={{ backgroundColor: item.color }}
                      />
                      <Box>
                        <Typography className="!text-[12px] !font-bold !text-slate-700">
                          {item.name}
                        </Typography>
                        <Typography className="!text-[12px] !font-semibold !text-slate-500">
                          {item.value.toLocaleString()} ({formatRate(percent)})
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box className="rounded-lg border border-slate-200 p-3">
            <Box className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <Typography className="!text-[14px] !font-bold !text-slate-700">
                {t("Top / Bottom by Eligibility %")}
              </Typography>
              <Box className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                {[
                  { value: "top", label: "Top 3" },
                  { value: "bottom", label: "Bottom 3" },
                ].map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => setRankMode(item.value)}
                    className={`rounded px-3 py-1.5 text-[12px] font-bold ${
                      rankMode === item.value
                        ? "bg-emerald-500 text-white"
                        : "text-slate-600"
                    }`}
                  >
                    {t(item.label)}
                  </button>
                ))}
              </Box>
            </Box>
            <Box className="space-y-2">{renderRankRows(rankRows)}</Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

const ClaimsAnalytics = ({ data, t }) => {
  const breakdown = data.claims_status_breakdown || {};
  const reasons = data.claims_rejection_reasons || [];
  const statusRows = [
    {
      key: "accepted",
      label: "Accepted",
      color: chartColors.accepted,
      metric: breakdown.accepted || {},
    },
    {
      key: "partial",
      label: "Partial",
      color: chartColors.partial,
      metric: breakdown.partial || {},
    },
    {
      key: "pending",
      label: "Queued",
      color: "#F59E0B",
      metric: breakdown.pending || {},
    },
    {
      key: "rejected",
      label: "Rejected",
      color: chartColors.rejected,
      metric: breakdown.rejected || {},
    },
    {
      key: "cancelled",
      label: "Cancelled",
      color: chartColors.cancelled,
      metric: breakdown.cancelled || {},
    },
    {
      key: "errored",
      label: "Errored",
      color: chartColors.errored,
      metric: breakdown.errored || {},
    },
  ];
  const totalCount = breakdown.total_count || data.total_claims || 0;
  const chartData = statusRows.map((row) => {
    const value = row.metric.count || 0;
    return {
      name: t(row.label),
      value,
      color: row.color,
      percentage: totalCount ? (value / totalCount) * 100 : 0,
    };
  });
  const primaryRows = chartData.filter((row) => row.value > 0);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} lg={7}>
        <ChartCard
          title={t("Claims by Status")}
          subtitle={`${totalCount.toLocaleString()} ${t("total claims")}`}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box className="relative h-[230px]">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={primaryRows.length ? primaryRows : chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={68}
                      outerRadius={98}
                      dataKey="value"
                      stroke="#fff"
                      strokeWidth={3}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`claim-status-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <Box className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <Typography className="!text-[24px] !font-bold !text-slate-900">
                    {totalCount.toLocaleString()}
                  </Typography>
                  <Typography className="!text-[11px] !font-semibold !text-slate-500">
                    {t("Total Claims")}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box className="space-y-3">
                {chartData.map((row) => (
                  <Box key={row.name}>
                    <Box className="mb-1 flex items-center justify-between gap-3">
                      <Box className="flex min-w-0 items-center gap-2">
                        <Box
                          className="h-2.5 w-2.5 rounded-sm"
                          sx={{ backgroundColor: row.color }}
                        />
                        <Typography className="truncate !text-[12px] !font-bold !text-slate-700">
                          {row.name}
                        </Typography>
                      </Box>
                      <Typography className="!text-[12px] !font-bold !text-slate-800">
                        {row.value.toLocaleString()}{" "}
                        <span className="font-semibold text-slate-400">
                          ({formatRate(row.percentage)})
                        </span>
                      </Typography>
                    </Box>
                    <Box className="h-2 overflow-hidden rounded-full bg-slate-100">
                      <Box
                        className="h-full rounded-full"
                        sx={{
                          width: `${Math.min(row.percentage, 100)}%`,
                          backgroundColor: row.color,
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </ChartCard>
      </Grid>

      <Grid item xs={12} lg={5}>
        <Box className="grid h-full gap-2">
          <ChartCard title={t("Top Rejection Reasons")}>
            <Box className="space-y-2">
              {reasons.length ? (
                reasons.map((reason, index) => (
                  <Box
                    key={`${reason.reason || "reason"}-${index}`}
                    className="grid grid-cols-[24px_minmax(0,1fr)_64px] items-center gap-2 rounded border border-slate-100 px-2 py-2"
                  >
                    <Typography className="!text-[12px] !font-semibold !text-slate-500">
                      {index + 1}
                    </Typography>
                    <Box className="min-w-0">
                      <Box className="mb-1 flex items-center justify-between gap-2">
                        <Typography className="truncate !text-[12px] !font-semibold !text-slate-700">
                          {reason.reason || t("Unknown")}
                        </Typography>
                        <Typography className="!text-[12px] !font-bold !text-slate-800">
                          {(reason.count || 0).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                        <Box
                          className="h-full rounded-full bg-red-500"
                          sx={{ width: `${Math.min(reason.percentage || 0, 100)}%` }}
                        />
                      </Box>
                    </Box>
                    <Typography className="!text-right !text-[12px] !font-bold !text-slate-600">
                      {formatRate(reason.percentage)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box className="flex h-[120px] items-center justify-center text-sm font-semibold text-gray-400">
                  {t("No rejection reasons found")}
                </Box>
              )}
            </Box>
          </ChartCard>
        </Box>
      </Grid>
    </Grid>
  );
};

const Dashboard = ({ data }) => {
  const { t } = useTranslation();

  // Calculate percentages
  const activeVerificationRate =
    ((data.active_verification / data.total_verification) * 100).toFixed(1) ||
    0;

  return (
    <Box>
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={12} md={3}>
          <MetricCard
            title={t("Total Patients")}
            value={data.patients}
            icon={<People fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            title={t("Total Eligibilities")}
            value={data.total_verification}
            icon={<CheckCircle fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            title={t("Total Claims")}
            value={data.total_claims}
            icon={<Assignment fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <MetricCard
            title={t("Total Pre-Auth")}
            value={data.total_pre_auth}
            icon={<CheckCircle fontSize="small" />}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <ChartCard
            title={t("Eligibility Percentage by Insurance Company")}
            subtitle={`${data.total_verification} ${t("total verifications")}`}
            title2={t("Active Rate")}
            subtitle2={`${activeVerificationRate}%`}
          >
            <Box sx={{ width: "100%", minHeight: 300, mt: 1 }}>
              <EligibilityPayerBreakdown
                rows={data.verification_payer_breakdown}
                totalVerification={data.total_verification}
                activeVerification={data.active_verification}
                notActiveVerification={data.not_active_verification}
                activeRate={Number(activeVerificationRate) || 0}
                t={t}
              />
            </Box>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={8}>
          <ChartCard
            title={t("Preauth Breakdown")}
            subtitle={`${data.total_pre_auth} ${t("total preauth requests")}`}
          >
            <Box sx={{ width: "100%", minHeight: 250, mt: 1 }}>
              <PreAuthBreakdownChart
                rows={data.pre_auth_breakdown}
                totalPreAuth={data.total_pre_auth}
                t={t}
              />
            </Box>
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={4}>
          <PreAuthDistribution
            rows={data.pre_auth_breakdown}
            totalPreAuth={data.total_pre_auth}
            t={t}
          />
        </Grid>

        <Grid item xs={12}>
          <ClaimsAnalytics data={data} t={t} />
        </Grid>
      </Grid>
    </Box>
  );
};
export default Dashboard;
